"use client"

import React, { useState, useCallback, lazy, Suspense, useEffect, useRef } from "react"
import { Search, X, MapPin, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover"
import {
  Dialog,
  DialogContent,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog"
import { ScrollArea } from "../ui/scroll-area"
import { cn, getSharedCookie, setSharedCookie } from "../lib/utils"

export interface ClockLocation {
  id: string
  label: string
  timezone: string
  lat: number
  lon: number
}

const STORAGE_KEY = "AskFinz_world_clock_locations"
const MAX_LOCATIONS = 4
const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search"
const SEARCH_DEBOUNCE_MS = 400
const MIN_QUERY_LENGTH = 2
const DEFAULT_API_BASE = typeof process !== "undefined" ? process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000" : "http://localhost:8000"

const TimezoneMapInner = lazy(() =>
  import("./timezone-map-inner").then((m) => ({ default: m.TimezoneMapInner }))
)

function formatTime(timezone: string, lon?: number): string {
  try {
    if (timezone === "UTC" && lon != null && !Number.isNaN(lon)) {
      const offsetHours = lon / 15
      const offsetMs = offsetHours * 60 * 60 * 1000
      return new Date(Date.now() + offsetMs).toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    }
    return new Date().toLocaleTimeString(undefined, {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  } catch {
    return "--:--"
  }
}

function formatTimezoneShort(timezone: string, lon?: number): string {
  if (timezone === "UTC" && lon != null && !Number.isNaN(lon)) {
    const offsetHours = lon / 15
    const sign = offsetHours >= 0 ? "+" : ""
    const h = Math.floor(Math.abs(offsetHours))
    const m = Math.round((Math.abs(offsetHours) - h) * 60)
    const pad = (n: number) => n.toString().padStart(2, "0")
    return `UTC${sign}${h}${m ? ":" + pad(m) : ""}`
  }
  try {
    const formatter = new Intl.DateTimeFormat(undefined, { timeZone: timezone, timeZoneName: "short" })
    const parts = formatter.formatToParts(new Date())
    const tzPart = parts.find((p) => p.type === "timeZoneName")
    return tzPart?.value ?? timezone.split("/").pop() ?? timezone
  } catch {
    return timezone.split("/").pop() ?? timezone
  }
}

export function getStoredClockLocations(): ClockLocation[] {
  if (typeof window === "undefined") return []
  try {
    const raw = getSharedCookie(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as ClockLocation[]
    return Array.isArray(parsed) ? parsed.slice(0, MAX_LOCATIONS) : []
  } catch {
    return []
  }
}

export function setStoredClockLocations(locations: ClockLocation[]) {
  if (typeof window === "undefined") return
  try {
    setSharedCookie(STORAGE_KEY, JSON.stringify(locations.slice(0, MAX_LOCATIONS)))
  } catch { }
}

interface TimezoneMapDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  locations: ClockLocation[]
  onSave: (locations: ClockLocation[]) => void
  apiBaseUrl?: string
  trigger?: React.ReactNode
}

export function TimezoneMapDialog({
  open,
  onOpenChange,
  locations,
  onSave,
  apiBaseUrl = DEFAULT_API_BASE,
  trigger,
}: TimezoneMapDialogProps) {
  const [query, setQuery] = useState("")
  const [searchResults, setSearchResults] = useState<Array<{ display_name: string; lat: string; lon: string }>>([])
  const [searching, setSearching] = useState(false)
  const [addingId, setAddingId] = useState<string | null>(null)
  const [selected, setSelected] = useState<ClockLocation[]>([])
  const searchAbortRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const runSearch = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < MIN_QUERY_LENGTH) {
      setSearchResults([])
      return
    }
    searchAbortRef.current?.abort()
    searchAbortRef.current = new AbortController()
    setSearching(true)
    setSearchResults([])
    try {
      const params = new URLSearchParams({
        q: q.trim(),
        format: "json",
        limit: "8",
      })
      const res = await fetch(`${NOMINATIM_URL}?${params}`, {
        headers: { Accept: "application/json" },
        signal: searchAbortRef.current.signal,
      })
      const data = await res.json()
      setSearchResults(Array.isArray(data) ? data : [])
    } catch (e) {
      if ((e as Error).name !== "AbortError") {
        console.error("Geocode error:", e)
        setSearchResults([])
      }
    } finally {
      setSearching(false)
    }
  }, [])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (query.trim().length < MIN_QUERY_LENGTH) {
      setSearchResults([])
      return
    }
    debounceRef.current = setTimeout(() => runSearch(query), SEARCH_DEBOUNCE_MS)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, runSearch])

  const search = useCallback(() => runSearch(query), [query, runSearch])

  const addLocation = useCallback(
    async (display_name: string, lat: string, lon: string) => {
      const latN = parseFloat(lat)
      const lonN = parseFloat(lon)
      if (Number.isNaN(latN) || Number.isNaN(lonN)) return
      setSelected((prev) => {
        if (prev.length >= MAX_LOCATIONS) {
          toast.warning("Maximum 4 locations", { description: "Remove one to add another." })
          return prev
        }
        return prev
      })
      const currentCount = selected.length
      if (currentCount >= MAX_LOCATIONS) return
      const resultId = `loc-${Date.now()}-${Math.random().toString(36).slice(2)}`
      const label = display_name.split(",").slice(0, 2).join(",").trim()
      setAddingId(resultId)
      let timezone = "UTC"
      try {
        const res = await fetch(
          `${apiBaseUrl}/api/timezone?lat=${latN}&lon=${lonN}`
        )
        if (res.ok) {
          const data = await res.json()
          timezone = (data.timezone as string) || timezone
        } else {
          toast.warning("Timezone guessed as UTC", { description: "Backend unavailable; time may be approximate." })
        }
      } catch (e) {
        console.error("Timezone lookup error:", e)
        toast.warning("Timezone guessed as UTC", { description: "Check backend is running at " + apiBaseUrl })
      }
      const loc: ClockLocation = {
        id: resultId,
        label,
        timezone,
        lat: latN,
        lon: lonN,
      }
      setSelected((prev) => {
        const exists = prev.some((l) => Math.abs(l.lat - loc.lat) < 0.01 && Math.abs(l.lon - loc.lon) < 0.01)
        if (exists) return prev
        const next = [...prev, loc].slice(0, MAX_LOCATIONS)
        if (next.length > prev.length) toast.success("Added", { description: label })
        return next
      })
      setAddingId(null)
    },
    [apiBaseUrl, selected.length]
  )

  const removeLocation = useCallback((id: string) => {
    setSelected((prev) => prev.filter((l) => l.id !== id))
  }, [])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        onSave(selected)
      }
      onOpenChange(next)
    },
    [onOpenChange, onSave, selected]
  )

  React.useEffect(() => {
    if (open) setSelected(locations)
  }, [open, locations])

  const content = (
    <div className="w-[min(90vw,420px)] rounded-lg border bg-popover text-popover-foreground shadow-md overflow-hidden">
      <div className="p-3 border-b bg-muted/30">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search city (results update as you type)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            className="pl-8 bg-background border-border"
            autoComplete="off"
          />
        </div>
      </div>
      <div className="border-b p-2 min-h-[200px]">
        <Suspense fallback={<div className="h-[200px] bg-muted/30 animate-pulse rounded" />}>
          <TimezoneMapInner locations={selected} className="rounded-md overflow-hidden" />
        </Suspense>
      </div>
      <ScrollArea className="max-h-[240px]">
        {searchResults.length > 0 && (
          <ul className="p-2 space-y-1">
            {searchResults.map((r, i) => {
              const key = `${r.lat}-${r.lon}-${i}`
              const isAdding = addingId !== null
              return (
                <li key={key}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-muted text-sm flex items-center gap-2 disabled:opacity-70"
                    disabled={isAdding}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      addLocation(r.display_name, r.lat, r.lon)
                    }}
                  >
                    <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate">{r.display_name}</span>
                    {isAdding && <Loader2 className="h-4 w-4 shrink-0 animate-spin ml-auto" />}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
        <div className="p-3 border-t">
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Selected locations ({selected.length}/{MAX_LOCATIONS})
          </div>
          {selected.length === 0 ? (
            <p className="text-sm text-muted-foreground">Search and click a result to add.</p>
          ) : (
            <ul className="space-y-2">
              {selected.map((loc) => (
                <li
                  key={loc.id}
                  className="flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <div>
                    <div className="font-medium">{loc.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatTimezoneShort(loc.timezone, loc.lon)} · {formatTime(loc.timezone, loc.lon)}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => removeLocation(loc.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </ScrollArea>
    </div>
  )

  if (trigger !== undefined) {
    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent className="p-0 w-auto border-0 shadow-none bg-transparent" align="end" sideOffset={8}>
          {content}
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          hideCloseButton={false}
          className="left-[50%] top-[calc(3.5rem+12px)] translate-x-[-50%] translate-y-0 max-w-[min(90vw,420px)] p-0 gap-0"
        >
          <DialogTitle className="sr-only">World Clock Settings</DialogTitle>
          <DialogDescription className="sr-only">
            Search and add up to 4 world clock locations to monitor different time zones on the map.
          </DialogDescription>
          {content}
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
